import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Remembers the open/closed state of facet accordions (keyed by their filter
 * param) for the life of the page. Applying a filter re-renders the facets
 * section, and the morph can recreate these accordion elements; restoring the
 * remembered state on connect keeps the user's expanded/collapsed choice
 * instead of snapping back to the server default.
 * @type {Map<string, boolean>}
 */
const facetOpenStateByParam = new Map();

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#restoreOrSetDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    // Remember facet accordion state so it survives section re-renders.
    this.details.addEventListener('toggle', this.#persistOpenState, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Restores a facet accordion's remembered open state (so it persists across
   * filter re-renders), falling back to the breakpoint default the first time.
   */
  #restoreOrSetDefaultOpenState() {
    const param = this.dataset.filterParamName;

    if (param && facetOpenStateByParam.has(param)) {
      this.details.open = /** @type {boolean} */ (facetOpenStateByParam.get(param));
    } else {
      this.#setDefaultOpenState();
    }
  }

  /** Persists a facet accordion's open state, keyed by its filter param. */
  #persistOpenState = () => {
    const param = this.dataset.filterParamName;
    if (param) facetOpenStateByParam.set(param, this.details.open);
  };

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const openOnMobile = this.hasAttribute('open-by-default-on-mobile');
    const openOnDesktop = this.hasAttribute('open-by-default-on-desktop');

    // When both breakpoints share the same default, the server-rendered `open`
    // attribute is already correct for either, so leave the current state
    // untouched. This preserves the user's open/closed choice across section
    // re-renders (e.g. applying sidebar filters) instead of snapping back to
    // the default. Only override when the breakpoints disagree and a single
    // rendered state can't satisfy both.
    if (openOnMobile === openOnDesktop) return;

    const isMobile = isMobileBreakpoint();
    this.details.open = (isMobile && openOnMobile) || (!isMobile && openOnDesktop);
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
